/**
 * usePermissionAccess Hook
 * Checks the RBAC-resolved per-module access tier (read/edit/full) from
 * user.effectivePermissions. Sibling to usePlanFeature (plan-based) and
 * useModuleAccess (module visibility) — this one is for gating a specific
 * ACTION within a page that's already visible, e.g. "can this user delete,
 * not just view, this record."
 */

import { useAuth } from './useAuth';
import type { PermissionAccessLevel } from '@/store/slices/authSlice';

const LEVEL_RANK: Record<PermissionAccessLevel, number> = { read: 1, edit: 2, full: 3 };

export const usePermissionAccess = () => {
  const { user } = useAuth();

  /**
   * True if the user's resolved permission set grants at least
   * `requiredLevel` on `module`. Fails CLOSED (denies) when
   * effectivePermissions hasn't been populated yet — unlike
   * useModuleAccess's transitional leniency for module VISIBILITY, this
   * gates actual mutating actions, where a false "allow" is worse than a
   * false "deny" that self-heals on the next /auth/me refresh. The backend's
   * own requirePermission is the real enforcement boundary regardless — this
   * is UX, not the security check itself.
   */
  const hasPermission = (module: string, requiredLevel: PermissionAccessLevel = 'read'): boolean => {
    if (user?.role === 'superadmin') return true;
    const effectivePermissions = user?.effectivePermissions;
    if (!effectivePermissions) return false;
    if (effectivePermissions === 'ALL_ACCESS') return true;
    const granted = effectivePermissions[module];
    if (!granted) return false;
    return LEVEL_RANK[granted] >= LEVEL_RANK[requiredLevel];
  };

  return { hasPermission, effectivePermissions: user?.effectivePermissions };
};
